/**
 * Version-based Cache Invalidation System
 *
 * When APP_VERSION changes, all browser caches are cleared and a hard reload is forced.
 * This ensures users always get the latest code after deployments.
 *
 * To trigger a refresh for all users:
 * 1. Increment APP_VERSION below
 * 2. Deploy the changes
 * 3. Users will automatically get the new version on their next page load
 */

const APP_VERSION = '1.0.2';  // Fixed opacity sync in collaboration mode
const VERSION_STORAGE_KEY = 'coandacloud_app_version';

(function() {
  // Migrate old localStorage keys from coandaview to coandacloud
  // This must run BEFORE any auth checks to prevent redirect loops
  migrateLocalStorageKeys();

  function migrateLocalStorageKeys() {
    const migrations = [
      { old: 'coandaview_token', new: 'coandacloud_token' },
      { old: 'coandaview_user', new: 'coandacloud_user' },
      { old: 'coandaview-sidebar-hidden', new: 'coandacloud-sidebar-hidden' },
      { old: 'coandaview_app_version', new: 'coandacloud_app_version' }
    ];

    let migrated = false;
    for (const { old: oldKey, new: newKey } of migrations) {
      const oldValue = localStorage.getItem(oldKey);
      if (oldValue !== null) {
        // Only migrate if new key doesn't already exist
        if (localStorage.getItem(newKey) === null) {
          localStorage.setItem(newKey, oldValue);
          console.log(`[Version] Migrated ${oldKey} → ${newKey}`);
        }
        // Remove old key
        localStorage.removeItem(oldKey);
        migrated = true;
      }
    }

    // Also clear old IndexedDB cache name if it exists
    if (window.indexedDB) {
      indexedDB.deleteDatabase('coandaview-geometry-cache');
    }

    if (migrated) {
      console.log('[Version] localStorage migration complete');
    }
  }

  const storedVersion = localStorage.getItem(VERSION_STORAGE_KEY);

  if (storedVersion && storedVersion !== APP_VERSION) {
    console.log(`[Version] Upgrading from ${storedVersion} to ${APP_VERSION}`);

    // Clear all caches
    clearAllCaches().then(() => {
      // Store new version
      localStorage.setItem(VERSION_STORAGE_KEY, APP_VERSION);

      // Force hard reload (bypass browser cache)
      console.log('[Version] Caches cleared, performing hard reload...');
      window.location.reload(true);
    });
  } else if (!storedVersion) {
    // First time visitor
    console.log(`[Version] First visit, version ${APP_VERSION}`);
    localStorage.setItem(VERSION_STORAGE_KEY, APP_VERSION);
  } else {
    console.log(`[Version] Current version: ${APP_VERSION}`);
  }

  async function clearAllCaches() {
    const promises = [];

    // Clear IndexedDB geometry cache
    if (window.indexedDB) {
      promises.push(new Promise((resolve) => {
        const deleteRequest = indexedDB.deleteDatabase('coandacloud-geometry-cache');
        deleteRequest.onsuccess = () => {
          console.log('[Version] IndexedDB geometry cache cleared');
          resolve();
        };
        deleteRequest.onerror = () => {
          console.warn('[Version] Failed to clear IndexedDB');
          resolve();
        };
        deleteRequest.onblocked = () => {
          console.warn('[Version] IndexedDB deletion blocked');
          resolve();
        };
      }));
    }

    // Clear Service Worker caches (if any)
    if ('caches' in window) {
      promises.push(
        caches.keys().then(names => {
          return Promise.all(names.map(name => {
            console.log('[Version] Clearing cache:', name);
            return caches.delete(name);
          }));
        }).catch(err => {
          console.warn('[Version] Failed to clear caches:', err);
        })
      );
    }

    // Clear session storage
    try {
      sessionStorage.clear();
      console.log('[Version] Session storage cleared');
    } catch (e) {
      console.warn('[Version] Failed to clear session storage');
    }

    await Promise.all(promises);
  }
})();

// Export for external access
window.APP_VERSION = APP_VERSION;
